import { useState, useEffect, useCallback, useRef } from "react";
import { GetAllTipos } from '@services/Tipo/Index.jsx';
import { GetAllProcessos } from '@services/Processo/Index.jsx';
import { GetAllByYear, GetAllYears } from "@services/Servico/Index.jsx";
import { useFetchPaginatedData } from "@hooks/api/useFetchPaginatedData.js";

export function useFetchDataCascade() {
  const [yearOptions, setYearOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);

  const [yearLoading, setYearLoading] = useState(false);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [typeLoading, setTypeLoading] = useState(false);
  const [processLoading, setProcessLoading] = useState(false);

  const isMounted = useRef(true);

  const {
    data: allTypes,
    loading: paginatedTypeLoading,
    fetchAllData: fetchAllTypes
  } = useFetchPaginatedData(GetAllTipos);

  const {
    data: allProcesses,
    loading: paginatedProcessLoading,
    fetchAllData: fetchAllProcessos
  } = useFetchPaginatedData(GetAllProcessos);

  useEffect(() => {
    isMounted.current = true;
    fetchAllTypes();
    fetchAllProcessos();
    return () => { isMounted.current = false; };
  }, []);

  const fetchYears = useCallback(async () => {
    setYearLoading(true);
    try {
      const response = await GetAllYears();
      const sorted = [...response].sort((a, b) => a - b);
      const options = sorted.map(year => ({ value: year, label: year }));
      if (isMounted.current) setYearOptions(options);
    } catch (err) {
      console.error("Erro ao buscar anos:", err);
    } finally {
      if (isMounted.current) setYearLoading(false);
    }
  }, []);

  const fetchServicesByYear = useCallback(async (year) => {
    setServiceLoading(true);
    try {
      const response = await GetAllByYear(year);
      if (Array.isArray(response)) {
        const sorted = response.sort((a, b) => a.servico.localeCompare(b.servico));
        const options = sorted.map(item => ({ value: item.id, label: item.servico }));
        if (isMounted.current) setServiceOptions(options);
      } else {
        if (isMounted.current) setServiceOptions([]);
      }
    } catch (err) {
      console.error(`Erro ao buscar serviços para o ano ${year}:`, err);
    } finally {
      if (isMounted.current) setServiceLoading(false);
    }
  }, []);

  const fetchTypesByServiceId = useCallback(async (serviceId) => {
    setTypeLoading(true);
    try {
      const filtered = allTypes.filter(item => item.idTipoServico === serviceId);
      const sorted = filtered
        .map(item => ({ value: item.id, label: item.tipo }))
        .sort((a, b) => a.label.localeCompare(b.label));
      if (isMounted.current) setTypeOptions(sorted);
    } catch (err) {
      console.error(`Erro ao buscar tipos para o serviço ${serviceId}:`, err);
      if (isMounted.current) setTypeOptions([]);
    } finally {
      if (isMounted.current) setTypeLoading(false);
    }
  }, [allTypes]);

  const fetchProcessosByType = useCallback(async (typeId) => {
    setProcessLoading(true);
    try {
      const filtered = allProcesses.filter(item => item.idTipoServicoAssociado === typeId);
      const sorted = filtered
        .map(item => ({ value: item.id, label: item.nomeProcesso }))
        .sort((a, b) => a.label.localeCompare(b.label));
      if (isMounted.current) setProcessOptions(sorted);
    } catch (err) {
      console.error("Erro ao buscar processos:", err);
      if (isMounted.current) setProcessOptions([]);
    } finally {
      if (isMounted.current) setProcessLoading(false);
    }
  }, [allProcesses]);

  return {
    yearOptions,
    setYearOptions,
    serviceOptions,
    setServiceOptions,
    typeOptions,
    setTypeOptions,
    processOptions,
    setProcessOptions,
    yearLoading,
    serviceLoading,
    typeLoading: typeLoading || paginatedTypeLoading,
    processLoading: processLoading || paginatedProcessLoading,
    fetchYears,
    fetchServicesByYear,
    fetchTypesByServiceId,
    fetchProcessosByType,
  };
}
